import React, { useState, useEffect } from 'react';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Image,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Box,
  useDisclosure,
  useToast,
  IconButton,
  Tooltip
} from '@chakra-ui/react';
import { ExternalLinkIcon, RepeatIcon } from '@chakra-ui/icons';
import useWallet, { WALLET_STATUS } from '../store/useWallet.js';

/**
 * Wallet Button Component
 * Handles wallet connection, disconnection, and status display
 */
const WalletButton = ({ 
  size = 'md', 
  variant = 'solid', 
  colorScheme = 'blue',
  showBalance = true,
  showAdmin = true,
  ...props 
}) => {
  const {
    status,
    address,
    xrpBalance,
    xgcBalance,
    errorMessage,
    connectXaman,
    disconnect,
    fetchBalances,
    cancelConnection,
    cleanupExistingConnection
  } = useWallet();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [connectionData, setConnectionData] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const toast = useToast();

  // Computed values
  const isConnected = status === WALLET_STATUS.CONNECTED && address;
  const isLoading = status === WALLET_STATUS.CONNECTING;
  const truncatedAddress = address ? `${address.substring(0, 6)}...${address.substring(address.length - 4)}` : '';
  const hasRequiredTokens = xgcBalance > 0; // You can adjust this threshold

  /**
   * Handle wallet connection
   */
  const handleConnect = async () => {
    try {
      // Clean up any existing connection attempts before starting new one
      if (status === WALLET_STATUS.CONNECTING) {
        console.log('Already connecting - cleaning up previous attempt');
        if (typeof cancelConnection === 'function') {
          await cancelConnection();
        }
      }
      
      // Clean up any residual state
      if (typeof cleanupExistingConnection === 'function') {
        cleanupExistingConnection();
      }
      
      onOpen(); // Open modal first
      const result = await connectXaman({ autoOpenPopup: false });
      
      if (result.success) {
        setConnectionData(result);
        setShowQR(true);
        
        toast({
          title: 'Connection Request Created',
          description: 'Please complete the request in your XUMM wallet',
          status: 'info',
          duration: 5000,
          isClosable: true
        });
      } else {
        onClose();
        toast({
          title: 'Connection Failed',
          description: result.error?.message || 'Failed to create connection request',
          status: 'error',
          duration: 5000,
          isClosable: true
        });
      }
    } catch (error) {
      console.error('Connection error:', error);
      onClose();
      toast({
        title: 'Connection Error',
        description: error.message || 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  /**
   * Handle wallet disconnection
   */
  const handleDisconnect = async () => {
    try {
      const result = disconnect();
      
      if (result.success) {
        toast({
          title: 'Wallet Disconnected',
          description: 'Your wallet has been disconnected successfully',
          status: 'success',
          duration: 3000,
          isClosable: true
        });
      } else {
        toast({
          title: 'Disconnection Failed',
          description: result.error || 'Failed to disconnect wallet',
          status: 'error',
          duration: 5000,
          isClosable: true
        });
      }
    } catch (error) {
      console.error('Disconnection error:', error);
      toast({
        title: 'Disconnection Error',
        description: error.message || 'An unexpected error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  /**
   * Handle balance refresh
   */
  const handleRefreshBalance = async () => {
    try {
      await fetchBalances();
      toast({
        title: 'Balance Updated',
        description: 'Your token balance has been refreshed',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: 'Refresh Failed',
        description: 'Failed to refresh balance',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  /**
   * Open XUMM app with deep link
   */
  const openXummApp = () => {
    if (connectionData?.deepLink) {
      window.open(connectionData.deepLink, '_blank');
    }
  };

  /**
   * Close modal and reset state with cleanup
   */
  const handleModalClose = async () => {
    console.log('Modal closing, current status:', status);
    
    // If we're in connecting state, cancel the connection
    if (status === WALLET_STATUS.CONNECTING) {
      console.log('Modal closed during connection - cancelling connection attempt');
      if (typeof cancelConnection === 'function') {
        await cancelConnection();
      }
    }
    
    // Clean up any existing connection state
    if (typeof cleanupExistingConnection === 'function') {
      cleanupExistingConnection();
    }
    
    // Reset local state
    onClose();
    setShowQR(false);
    setConnectionData(null);
  };

  /**
   * Cleanup on component unmount
   */
  useEffect(() => {
    return () => {
      // Clean up any pending connections when component unmounts
      if (status === WALLET_STATUS.CONNECTING) {
        console.log('WalletButton unmounting - cleaning up connection');
        if (typeof cleanupExistingConnection === 'function') {
          cleanupExistingConnection();
        }
      }
    };
  }, [status, cleanupExistingConnection]);

  /**
   * Effect to handle connection status changes
   */
  useEffect(() => {
    if (status === WALLET_STATUS.CONNECTED && isOpen) {
      handleModalClose();
      toast({
        title: 'Wallet Connected!',
        description: `Successfully connected to ${truncatedAddress}`,
        status: 'success',
        duration: 5000,
        isClosable: true
      });
    }
  }, [status, isOpen, truncatedAddress, toast]);

  /**
   * Effect to handle errors
   */
  useEffect(() => {
    if (errorMessage && isOpen) {
      toast({
        title: 'Connection Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  }, [errorMessage, isOpen, toast]);

  /**
   * Render connection status
   */
  const renderConnectionStatus = () => {
    switch (status) {
      case WALLET_STATUS.CONNECTING:
        return (
          <HStack spacing={2}>
            <Spinner size="sm" />
            <Text>Connecting...</Text>
          </HStack>
        );
      
      case WALLET_STATUS.VERIFYING:
        return (
          <HStack spacing={2}>
            <Spinner size="sm" />
            <Text>Verifying...</Text>
          </HStack>
        );
      
      default:
        return null;
    }
  };

  /**
   * Render wallet info when connected
   */
  const renderWalletInfo = () => {
    if (!isConnected) return null;

    return (
      <VStack spacing={2} align="stretch">
        <HStack justify="space-between">
          <Text fontSize="sm" color="gray.600">
            Wallet Address:
          </Text>
          <Text fontSize="sm" fontFamily="mono">
            {truncatedAddress}
          </Text>
        </HStack>

        {showBalance && (
          <>
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.600">
                XRP Balance:
              </Text>
              <HStack>
                <Text fontSize="sm">
                  {xrpBalance.toFixed(2)} XRP
                </Text>
              </HStack>
            </HStack>

            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.600">
                XGC Balance:
              </Text>
              <HStack>
                <Text 
                  fontSize="sm" 
                  color={hasRequiredTokens ? 'green.500' : 'red.500'}
                >
                  {xgcBalance.toLocaleString()} XGC
                </Text>
                <Tooltip label="Refresh Balance">
                  <IconButton
                    size="xs"
                    icon={<RepeatIcon />}
                    onClick={handleRefreshBalance}
                    isLoading={isLoading}
                    variant="ghost"
                  />
                </Tooltip>
              </HStack>
            </HStack>

            {!hasRequiredTokens && (
              <Alert status="warning" size="sm">
                <AlertIcon />
                <AlertDescription fontSize="xs">
                  Insufficient XGC tokens for full access
                </AlertDescription>
              </Alert>
            )}
          </>
        )}

        {showAdmin && xgcBalance >= 1000 && (
          <Alert status="info" size="sm">
            <AlertIcon />
            <AlertDescription fontSize="xs">
              Admin privileges active
            </AlertDescription>
          </Alert>
        )}
      </VStack>
    );
  };

  /**
   * Main render
   */
  if (isConnected) {
    return (
      <VStack spacing={3} {...props}>
        <Button
          size={size}
          variant="outline"
          colorScheme="green"
          onClick={handleDisconnect}
          isLoading={isLoading}
          loadingText="Disconnecting..."
        >
          {truncatedAddress} ✓
        </Button>
        {renderWalletInfo()}
      </VStack>
    );
  }

  return (
    <>
      <Button
        size={size}
        variant={variant}
        colorScheme={colorScheme}
        onClick={handleConnect}
        isLoading={status === WALLET_STATUS.CONNECTING}
        loadingText="Connecting..."
        {...props}
      >
        Connect Wallet
      </Button>

      {/* Connection Modal */}
      <Modal 
        isOpen={isOpen} 
        onClose={handleModalClose}
        closeOnOverlayClick={false}
        size="md"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Connect Your XUMM Wallet
          </ModalHeader>
          <ModalCloseButton />
          
          <ModalBody pb={6}>
            <VStack spacing={4}>
              {renderConnectionStatus()}

              {errorMessage && (
                <Alert status="error">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Connection Error</AlertTitle>
                    <AlertDescription>{errorMessage}</AlertDescription>
                  </Box>
                </Alert>
              )}

              {showQR && connectionData?.qrUrl && (
                <VStack spacing={3}>
                  <Text textAlign="center" color="gray.600">
                    Scan this QR code with your XUMM wallet or tap the button below:
                  </Text>
                  
                  <Box
                    p={4}
                    bg="white"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    <Image
                      src={connectionData.qrUrl}
                      alt="XUMM QR Code"
                      maxW="200px"
                      mx="auto"
                    />
                  </Box>

                  <Button
                    leftIcon={<ExternalLinkIcon />}
                    colorScheme="blue"
                    onClick={openXummApp}
                    size="lg"
                    width="full"
                  >
                    Open XUMM Wallet
                  </Button>

                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    Waiting for you to complete the request in XUMM...
                  </Text>
                </VStack>
              )}

              {status === WALLET_STATUS.CONNECTING && !showQR && (
                <VStack spacing={3}>
                  <Spinner size="lg" />
                  <Text>Creating connection request...</Text>
                </VStack>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default WalletButton;